import { Col, GetProps, Input, message, Select, Table, TableColumnsType, TableProps, Tooltip, Typography } from "antd";
import { useEffect, useState } from "react";
import { OptionFilter, StopVoc } from "src/entity/fugitive/fugitive.entity";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { getStopVoc, getYear } from "src/services/fugitive.service";
import { profileStore } from "src/stores/profile-store";
import { useShallow } from "zustand/react/shallow";
import { RiToolsFill } from "react-icons/ri";
import { excelStore } from "src/stores/excel-store.interface";
import { generateYearArray } from "src/utils/array";
import { formatTableText } from "src/components/AntdCustomPart/TableTitle.tsx/TableTitle";

type OnChange = NonNullable<TableProps<any>["onChange"]>;
type Filters = Parameters<OnChange>[1];
const { Text } = Typography;
const { Search } = Input;
type SearchProps = GetProps<typeof Input.Search>;

const StopVocTable = () => {
  const profile = profileStore(useShallow((state) => state.profile));
  const loadingProfile = profileStore(useShallow((state) => state.loading));
  const { permissions } = useNavigationMenu();
  const [companyId, setCompanyId] = useState<string>("");
  const [messageApi, contextHolder] = message.useMessage();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [optionYears, setOptionYears] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [current, setCurrent] = useState(1);
  const [total, setTotal] = useState<number>(0);
  const pageSize = 10;
  const [filter, setFilter] = useState<Filters>({});
  const [vocResult, setVocResult] = useState<StopVoc[]>([]);
  const [optionsFilter, setOptionFilter] = useState<OptionFilter[]>([]);
  const [textSearch, setTextSearch] = useState<string>("");
  const [searchTrigger, setSearchTrigger] = useState(0);

  const excelSetSelectedYear = excelStore(useShallow((state) => state.setSelectedYear));
  const excelSetQueryStr = excelStore(useShallow((state) => state.setQueryStr));

  const fetchYear = () => {
    const years = generateYearArray();
    setOptionYears(years as number[]);
    setSelectedYear(years[0] as number);
  };

  const onChangeYear = (value: number) => {
    setSelectedYear(value);
  };

  const calPagination = (current?: number) => {
    if (current) {
      return (current - 1) * 10;
    }
  };

  const createParamFilter = (type: "fetch" | "export") => {
    let queryParam = "";
    let offset = 0;
    let limit = pageSize;
    if (selectedYear) {
      queryParam += `year=${selectedYear}`;
    }

    if (type == "fetch") {
      if (current) {
        let resultCalOffset = calPagination(current);
        if (resultCalOffset) {
          offset = resultCalOffset;
        }
        queryParam += `&offset=${offset}`;
      }
      if (limit) {
        queryParam += `&limit=${limit}`;
      }
    }

    if (filter) {
      const str = Object.entries(filter)
        .filter(([, value]) => value)
        .map(([key, value]) => `&${key}=[${value?.join(",")}]`)
        .join("");
      queryParam += str;
    }

    if (textSearch) {
      queryParam += `&textSearch=${textSearch}`;
    }

    return queryParam;
  };

  const fetchStopVoc = async () => {
    try {
      if (selectedYear) {
        setLoading(true);
        let offset = 0;
        let resultCalOffset = calPagination(current);
        if (resultCalOffset) {
          offset = resultCalOffset;
        }

        const filterParams = createParamFilter("fetch");
        const queryString = new URLSearchParams(filterParams).toString();

        const response = await getStopVoc(queryString);
        if (response.data.code === 200) {
          const data = response.data.data.data;
          const total = response.data.data.total;
          let vocResult = data.map((el: any, index: number) => {
            return {
              no: (index + offset + 1).toString(),
              ...el,
            };
          });
          // console.log("vocResult", vocResult);
          setVocResult([...vocResult]);
          setTotal(total);
          //   if (canViewTable(permissions)) {
          //     setTankData([...tanks]);
          //     setTotal(total);
          //   } else {
          //     setTankData([]);
          //     setTotal(0);
          //   }
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onTableChange: TableProps<StopVoc>["onChange"] = (pagination: any, filters: any) => {
    let current = pagination.current;
    setFilter(filters);
    setCurrent(current);
  };

  const onSearch: SearchProps["onSearch"] = (value, _e, info) => {
    // console.log(info?.source, value);
    setTextSearch(value);
    setCurrent(1);
    setSearchTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    fetchYear();
  }, []);

  useEffect(() => {
    if (profile?.activeCompany) {
      const fetchData = async () => {
        const companyId = profile.activeCompany._id;
        if (companyId) {
          setSelectedYear(currentYear);
          setTextSearch("");
          setCompanyId(companyId);
        }
      };
      fetchData();
      setCurrent(1);
    }
  }, [profile?.activeCompany]);

  useEffect(() => {
    const fetchData = async () => {
      if (selectedYear) {
        const queryParam = createParamFilter("export");
        if (queryParam) {
          excelSetQueryStr(queryParam);
        }
        await Promise.all([fetchStopVoc()]);
      }
    };
    fetchData();
  }, [current, selectedYear, searchTrigger]);

  useEffect(() => {
    setCurrent(1);
  }, [selectedYear]);

  const columns: TableColumnsType = [
    {
      title: formatTableText("Area"),
      align: "center",
      dataIndex: "area",
      render: (_, record) => {
        let areaName = record?.areaDetails?.areaName || record.areaStr || "";
        return <Text>{areaName}</Text>;
      },
    },
    {
      title: formatTableText("Equipment ID", ["-"], 9),
      align: "center",
      dataIndex: "equipmentID",
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: formatTableText("Check Date"),
      align: "center",
      dataIndex: "checkdate",
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: formatTableText("VOCs Max (ppm)"),
      align: "center",
      dataIndex: "VOCmax",
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: formatTableText("Due Date"),
      align: "center",
      dataIndex: "dueDate",
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: formatTableText("Fixed Date"),
      align: "center",
      dataIndex: "fixedDate",
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: formatTableText("Fixed Result (ppm)"),
      align: "center",
      dataIndex: "fixedResult",
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: formatTableText("Comment"),
      align: "center",
      dataIndex: "fixedComment",
      render: (text) => (
        <Tooltip title={text}>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <RiToolsFill style={{ fontSize: "16px", cursor: "pointer" }} />
          </div>
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}
      <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: 10 }}>
        <Col span={4}>
          <Select
            value={selectedYear}
            style={{ width: 120 }}
            onChange={onChangeYear}
            options={optionYears.map((year) => ({ label: year, value: year }))}
            loading={loading || loadingProfile}
            size="middle"
          />
        </Col>

        <Col span={6}>
          <Search onSearch={onSearch} style={{ width: 250 }} value={textSearch} onChange={(e) => setTextSearch(e.target.value)} />
        </Col>

        <Col span={2}></Col>
      </div>
      <Table
        className="w-full whitespace-nowrap"
        columns={columns}
        dataSource={vocResult}
        onChange={onTableChange}
        rowKey="_id"
        scroll={{ x: "max-content" }}
        loading={loading || loadingProfile}
        pagination={{
          size: "small",
          showSizeChanger: false,
          current,
          pageSize,
          total,
        }}
      />
    </div>
  );
};

export default StopVocTable;
